import type { SeenMedplumClient } from '@seen/core/client'
import * as codeSystems from '@seen/core/codeSystems'
import { searchTerms } from '@seen/core/searchTerms'
import { capturableQuestions } from '@seen/core/terminology/capturable'
import { searchLoincPanels } from '@seen/core/terminology/instruments/loincPanels'
import {
  INSTRUMENT_TEMPLATES,
  instrumentItems,
  instrumentTemplate,
  instrumentShortName,
} from '@seen/core/terminology/instruments/registry'
import type { InstrumentTemplate } from '@seen/core/terminology/instruments/types'
import {
  libraryCodingDetails,
  TERMINOLOGY_LIBRARY,
} from '@seen/core/terminology/library'
import {
  localObservationHits,
  type ObservationSearchHit,
  observationSearch,
  rankObservationHits,
  standardObservationHit,
} from '@seen/core/terminology/observationSearch'
import {
  observationTemplate,
  templateDetails,
} from '@seen/core/terminology/observationTemplates'
import { shorthandServerSearch } from '@seen/core/terminology/shorthand'
import { ASSESSMENT_QUESTIONS_SYSTEM } from '@seen/core/terminology/types'
import { IconClipboardCheck } from '@tabler/icons-react'
import type { QueryClient } from '@tanstack/react-query'
import { compact, uniqBy } from 'lodash-es'
import { createElement } from 'react'
import type { ComboboxAsyncOption } from '#src/components/core/combobox/ComboboxAsync.tsx'
import type { ComboboxOption } from '#src/components/core/combobox/util.ts'
import { loincPanelHasForm } from '#src/components/rich-text-editor/mention/loincPanel.ts'
import {
  ObservationOptionLabel,
  type ObservationOptionLabelProps,
} from '#src/components/rich-text-editor/mention/ObservationOptionLabel.tsx'
import type { ValueSetAutocompleteValue } from '#src/components/ValueSetAutocomplete/toOptions.ts'
import { valueSetAutocompleteLoadOptions } from '#src/components/ValueSetAutocomplete/valueSetAutocompleteLoadOptions.ts'

const LIBRARY_MATCHES = 10
// LOINC names every panel as one ("Vital signs panel"). Medplum's `$expand`
// cannot leave panels out of the value set, so they are dropped here.
const PANEL_NAME = /\bpanel\b/i
// A search that names a panel or screening rather than a measurement.
const PANEL_QUERY =
  /\b(panel|set|survey|screen|screening|questionnaire|form|assessment|vitals?|phq|gad|audit|prapare|promis|cbc|bmp|cmp|lipid|metabolic)\b/i
// Rows asked of the terminology server for a ranked search, and the most of
// them shown.
const RANKED_FETCH = 30
const REMOTE_SHOWN = 12

type ObservationOption = ComboboxAsyncOption<ValueSetAutocompleteValue>

interface ObservationOptionsArgs {
  input: string
  signal: AbortSignal
  medplum: SeenMedplumClient
  includeLibrary: boolean
  // Validated tools recorded as observations (the structuredCapture flag).
  includeInstruments?: boolean
  // Rank shorthand and common codes first and tolerate typos
  // (the structured-capture prototype flag). The options come back in the order to show
  // them, so the picker must not re-rank them.
  ranked?: boolean
  // Leaves out LOINC panels NLM has no form for; without it they are offered.
  queryClient?: QueryClient
}

// The codes the observation picker offers. Question codes from our own
// library that can be recorded on their own come ahead of LOINC, which
// returns near misses for almost any search.
export async function loadObservationOptions(
  args: ObservationOptionsArgs
): Promise<ObservationOption[]> {
  return args.ranked ? rankedOptions(args) : unrankedOptions(args)
}

async function unrankedOptions({
  input,
  signal,
  medplum,
  includeLibrary,
  includeInstruments = false,
  queryClient,
}: ObservationOptionsArgs): Promise<ObservationOption[]> {
  // The terminology server matches each word as a word prefix, so it gets
  // the stemmed terms: "blood pressures" reaches it as "blood pressure".
  // A search of stopwords alone goes as typed.
  const terms = searchTerms(input).join(' ') || input
  const [standard, panels] = await Promise.all([
    standardOptions(terms, signal, medplum, includeInstruments),
    includeInstruments ? panelOptions(input, signal, queryClient) : [],
  ])
  return withPanels(
    [
      ...(includeInstruments ? instrumentOptions(input) : []),
      ...(includeLibrary ? libraryOptions(input) : []),
      ...standard,
    ],
    panels
  )
}

interface RankedHit extends ObservationSearchHit {
  option: ObservationOption
}

async function rankedOptions({
  input,
  signal,
  medplum,
  includeLibrary,
  includeInstruments = false,
  queryClient,
}: ObservationOptionsArgs): Promise<ObservationOption[]> {
  const search = observationSearch(input)
  const panels = includeInstruments
    ? panelOptions(search.corrected?.join(' ') || input, signal, queryClient)
    : Promise.resolve([])
  // The server cannot fuzz, and a word our dictionary lacks may still be a
  // LOINC word, so a search that looks misspelled goes both as typed and
  // corrected, with what the typed one finds first.
  const typed = standardOptions(
    search.terms.join(' ') || input,
    signal,
    medplum,
    includeInstruments,
    RANKED_FETCH
  )
  const corrected = search.corrected
    ? standardOptions(
        search.corrected.join(' '),
        signal,
        medplum,
        includeInstruments,
        RANKED_FETCH
      ).catch(() => [])
    : Promise.resolve([])
  // An instrument's alias reaches the server as the instrument's name, so
  // "phq9" finds the PHQ-9 codes LOINC names in full.
  const expansion = shorthandServerSearch(input)
  const expanded = expansion
    ? standardOptions(
        expansion,
        signal,
        medplum,
        includeInstruments,
        RANKED_FETCH
      ).catch(() => [])
    : Promise.resolve([])
  const standard = uniqBy(
    [...(await typed), ...(await corrected), ...(await expanded)],
    optionKey
  )
  const local = localObservationHits(input, search, {
    includeLibrary,
    includeInstruments,
  }).flatMap((hit): RankedHit[] => {
    const option = localOption(hit)
    return option ? [{ ...hit, option }] : []
  })
  const remote = standard.map((option): RankedHit => ({
    ...standardObservationHit(search, option.value),
    option,
  }))
  // Over-fetched so the re-ranking has the plain observable to choose from;
  // only the best of what the server returned are shown.
  const fromServer = new Set(remote.map(({ option }) => option))
  let shown = 0
  const options = rankObservationHits(search, [...local, ...remote])
    .filter(({ option }) => !fromServer.has(option) || ++shown <= REMOTE_SHOWN)
    .map(({ option }) => option)
  return withPanels(
    options,
    await panels,
    PANEL_QUERY.test(input) ? 1 : PANELS_AFTER
  )
}

// Rows the picker keeps for panels, after the first few matches, since a
// search names a measurement more often than a panel.
const PANELS_AFTER = 3

function optionKey({ value }: ObservationOption): string {
  return `${value.system}|${value.code}`
}

function withPanels(
  options: ObservationOption[],
  panels: ObservationOption[],
  after = PANELS_AFTER
): ObservationOption[] {
  const shown = new Set(options.map(optionKey))
  const extra = panels.filter((panel) => !shown.has(optionKey(panel)))
  return [...options.slice(0, after), ...extra, ...options.slice(after)]
}

// LOINC panels NLM finds for the search, placed from their NLM definition.
// A panel with an instrument in source is offered as that instrument. NLM
// being out leaves the rest of the search as it is.
async function panelOptions(
  input: string,
  signal: AbortSignal,
  queryClient: QueryClient | undefined
): Promise<ObservationOption[]> {
  const found = await searchLoincPanels(input, { signal }).catch(() => [])
  const panels = found.filter(
    (panel) =>
      !instrumentForPanel({
        system: panel.system ?? '',
        code: panel.code ?? '',
      })
  )
  const hasForm = await Promise.all(
    panels.map((panel) =>
      queryClient && panel.code
        ? loincPanelHasForm(queryClient, panel.code)
        : Promise.resolve(true)
    )
  )
  return panels
    .filter((_, index) => hasForm[index])
    .map((panel) =>
      standardOption(
        {
          system: panel.system ?? '',
          code: panel.code ?? '',
          display: panel.display ?? panel.code ?? '',
        },
        { panel: true }
      )
    )
}

// A row from our own sources: an instrument, one of our question codes, or a
// shorthand or common LOINC code, which must be recordable like any other.
function localOption(hit: ObservationSearchHit): ObservationOption | undefined {
  if (hit.instrument) {
    const template = instrumentTemplate(hit.instrument)
    return template ? instrumentOption(template) : undefined
  }
  const { system, code, display, definition } = hit
  if (system === ASSESSMENT_QUESTIONS_SYSTEM) {
    return libraryOption({ system, code, display, definition })
  }
  const value = { system, code, display }
  return isRecordable(value, false) ? standardOption(value) : undefined
}

// A panel with no template in source is recorded only from its NLM
// definition, which comes with structured capture.
function isRecordable(
  value: ValueSetAutocompleteValue,
  nlmPanels: boolean
): boolean {
  return (
    nlmPanels || !PANEL_NAME.test(value.display) || !!observationTemplate(value)
  )
}

async function standardOptions(
  filter: string,
  signal: AbortSignal,
  medplum: SeenMedplumClient,
  nlmPanels = false,
  countPerBinding?: number
): Promise<ObservationOption[]> {
  const options = await valueSetAutocompleteLoadOptions({
    input: filter,
    signal,
    medplum,
    binding: codeSystems.LOINC_OBSERVATIONS_VALUE_SET,
    toOption: (element) => standardOption(element),
    countPerBinding,
  })
  return options.filter(({ value }) => isRecordable(value, nlmPanels))
}

// `panel` marks an NLM panel result, whatever its name; any other row is a
// panel when LOINC names it one.
// A row for a code already known, such as one recorded for the participant.
export function observationCodeOption(
  element: ValueSetAutocompleteValue
): ObservationOption {
  return element.system === ASSESSMENT_QUESTIONS_SYSTEM
    ? libraryOption(element)
    : standardOption(element)
}

function standardOption(
  element: ValueSetAutocompleteValue,
  { panel = PANEL_NAME.test(element.display) }: { panel?: boolean } = {}
): ObservationOption {
  const template = observationTemplate(element)
  const labelProps: ObservationOptionLabelProps = {
    ...element,
    // A code recorded with its parts in one record is not placed as a panel.
    panel: panel && !template,
  }
  // A panel we record has its parts in source, so its row says them.
  if (template) {
    labelProps.details = {
      ...templateDetails(template),
      display: element.display,
    }
  }
  return {
    value: element,
    label: createElement(ObservationOptionLabel, labelProps),
    keywords: compact([element.code, element.display]),
  }
}

// The instrument whose panel code this is, when one is templated in source.
export function instrumentForPanel(
  coding: Pick<ValueSetAutocompleteValue, 'system' | 'code'>
): InstrumentTemplate | undefined {
  return INSTRUMENT_TEMPLATES.find(
    (template) =>
      template.panel.code === coding.code &&
      (!coding.system || template.panel.system === coding.system)
  )
}

// Instruments matching the search, as "Instrument · PHQ-9 · 9 items".
function instrumentOptions(input: string): ObservationOption[] {
  const query = input.trim().toLowerCase()
  return INSTRUMENT_TEMPLATES.filter(
    (template) =>
      !query ||
      template.title.toLowerCase().includes(query) ||
      template.id.toLowerCase().includes(query)
  ).map(instrumentOption)
}

// The coding the picker records an instrument under: its panel.
export function instrumentCoding(
  template: InstrumentTemplate
): ValueSetAutocompleteValue {
  return {
    system: template.panel.system ?? '',
    code: template.panel.code ?? '',
    display: template.title,
  }
}

// The rows of the picker's Instruments level, keyed by template id.
export function instrumentPickerOptions(): ComboboxOption<string>[] {
  return INSTRUMENT_TEMPLATES.map((template) => {
    const count = instrumentItems(template).length
    return {
      value: template.id,
      label: createElement(
        'span',
        { className: 'flex items-center gap-2' },
        createElement(
          'span',
          { className: 'p-0.5' },
          createElement(IconClipboardCheck, { className: 'size-4 shrink-0' })
        ),
        createElement(
          'span',
          null,
          template.title,
          createElement(
            'span',
            { className: 'text-muted-foreground' },
            ` · ${count} item${count === 1 ? '' : 's'}`
          )
        )
      ),
      keywords: compact([
        template.id,
        template.title,
        instrumentShortName(template),
      ]),
    }
  })
}

function instrumentOption(template: InstrumentTemplate): ObservationOption {
  const name = instrumentShortName(template)
  const count = instrumentItems(template).length
  return {
    value: instrumentCoding(template),
    label: createElement(
      'span',
      { className: 'text-sm' },
      `Instrument · ${name} · ${count} item${count === 1 ? '' : 's'}`
    ),
    keywords: compact([template.id, template.title, name]),
  }
}

// Our own question codes come from source, so they need no request and show
// how they are answered.
function libraryOptions(input: string): ObservationOption[] {
  return capturableQuestions(TERMINOLOGY_LIBRARY.questionCodes, input)
    .slice(0, LIBRARY_MATCHES)
    .map(libraryOption)
}

function libraryOption({
  system,
  code,
  display,
  definition,
}: {
  system: string
  code: string
  display: string
  definition?: string
}): ObservationOption {
  return {
    value: { system, code, display },
    label: createElement(ObservationOptionLabel, {
      system,
      code,
      display,
      definition,
      details: libraryCodingDetails({ system, code }),
    }),
    keywords: compact([code, display, definition]),
  }
}
